/**
 * --------------------------------------------------------------------------
 * Component Name : ProfitReports.jsx
 * Created Date   : 03-Nov-2025
 * Created By     : Sreeraj
 * Modified Date  : 04-Nov-2025
 * Modified By    : Sreeraj
 * Description    : This component displays the Profit Report section.
 * --------------------------------------------------------------------------
 */

import React, { useEffect, useState } from "react";
import {
    FormControl,
    Box,
    IconButton,
    Switch,
    Typography,
    Grid,
} from "@mui/material";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import "./ProfitReport.css";
import { ErrorToast, SuccessToast } from "../../../../toast/toasts";
import { CommonHelpers } from "../../../../services/commonHelpers";
import { get_profit_report } from "../../../../api/profitReportApis";
import { get_expense_profit_report } from "../../../../api/expenseReportApis";
import CustomAutoCompleteDropdown from "../../../../components/custom_auto_complete_dropdown/CustomAutoCompleteDropdown";
import CustomYearPicker from "../../../../components/custom_year_picker/CustomYearPicker";
import ExpandableView from "../../../../components/expandable_view/ExpandableView";

// ✅ Month options
const months = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];

const ProfitReports = () => {
    const currentMonth = new Date().getMonth() + 1;
    const formattedMonth = String(currentMonth).padStart(2, "0");
    const currentYear = new Date().getFullYear();

    // ✅ States
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [reportList, setReportList] = useState({
        grand_total: {},
        monthly_report: [],
        year: ''
    });
    const [totalProfit, setTotalProfit] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [isFinancialYear, setIsFinancialYear] = useState(false);
    const [selectedYear, setSelectedYear] = useState(currentYear);

    // ✅ Initial filter payload
    const [filterPayload, setFilterPayload] = useState({
        // month: `${currentYear}-${formattedMonth}`,
        year: dayjs(`${currentYear}-01-01`),
        location_id: "All",
        financial_year_start: CommonHelpers.getLastCompletedFinancialYear().start,
        financial_year_end: CommonHelpers.getLastCompletedFinancialYear().end,
        filter_type: "YEAR",
    });

    // ✅ Financial Year Dropdown Options
    const financialYearOptions = CommonHelpers.getFinancialYearList(2023, 3).map((fy) => ({
        label: fy.label,
        value: fy.value,
    }));

    // ✅ Fetch data when filters change
    useEffect(() => {
        getExpenseProfitReport();
    }, [pageNumber, totalItemPerPage, filterPayload]);

    // ✅ Fetch Profit Report
    const getExpenseProfitReport = async () => {
        const formData = new FormData();
        formData.append("page", pageNumber);
        formData.append("pageItem_count", totalItemPerPage);

        Object.entries(filterPayload).forEach(([key, value]) => {
            if (key === "year" && value) {
                const yearValue = value.year ? value.year() : new Date(value).getFullYear();
                formData.append(key, yearValue);
            } else if (key === "location_id") {
                formData.append(key, value === "All" ? "" : value);
            } else {
                formData.append(key, value);
            }
        });

        try {
            const response = await get_expense_profit_report(formData);
            if (response?.data?.status == "success") {
                // let data = response.data.data.data;

                // // ✅ Apply search filter
                // if (searchTerm.trim()) {
                //     const lower = searchTerm.toLowerCase();
                //     data = data.filter(
                //         (item) =>
                //             item.location_name?.toLowerCase().includes(lower) ||
                //             item.profit_date?.toLowerCase().includes(lower) ||
                //             item.remarks?.toLowerCase().includes(lower)
                //     );
                // }

                setReportList({
                    monthly_report: response.data.monthly_report,
                    grand_total: response.data.grand_total,
                    year: response.data.year
                });
                // setTotalPages(response.data.data.last_page);
                // setTotalProfit(response.data.total_profit || 0);
            } else {
                // setReportList([]);
                // setTotalPages(0);
                // setTotalProfit(0);
                // ErrorToast(response?.data?.message);
            }
        } catch (error) {
            console.error(error);
            ErrorToast("Something went wrong while fetching profit report");
        }
    };

    // ✅ Table Columns
    const Columns = [
        { title: "Sl.No", dataIndex: "key", key: "key" },
        { title: "Date", dataIndex: "profit_date", key: "profit_date" },
        { title: "Location", dataIndex: "location_name", key: "location_name" },
        { title: "Revenue", dataIndex: "revenue", key: "revenue" },
        { title: "Expense", dataIndex: "expense", key: "expense" },
        { title: "Profit", dataIndex: "profit", key: "profit" },
        { title: "Remarks", dataIndex: "remarks", key: "remarks" },
    ];

    // ✅ Data Source
    // const dataSource = reportList.map((item, index) => ({
    //     key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
    //     profit_date: item.profit_date,
    //     location_name: item.location_name,
    //     revenue: item.revenue,
    //     expense: item.expense,
    //     profit: item.profit,
    //     remarks: item.remarks,
    // }));

    // ✅ Export Excel
    const handleExport = async () => {
        try {
            const formData = new FormData();
            Object.entries(filterPayload).forEach(([key, value]) => {
                if (key === "year" && value) {
                    const yearValue = value.year ? value.year() : new Date(value).getFullYear();
                    formData.append(key, yearValue);
                } else if (key === "location_id") {
                    formData.append(key, value === "All" ? "" : value);
                } else {
                    formData.append(key, value);
                }
            });

            const response = await get_profit_report(formData);
            const rawData = response?.data?.data?.data || [];

            if (!rawData.length) {
                ErrorToast("No data available for export");
                return;
            }

            const exportData = rawData.map((item) => ({
                Date: item.profit_date,
                Location: item.location_name,
                Revenue: item.revenue,
                Expense: item.expense,
                Profit: item.profit,
                Remarks: item.remarks,
            }));

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: "A3" });

            const title = `Profit Report - ${dayjs(filterPayload.year).year()}`;
            const totalText = `Total Profit: ₹${response.data.total_profit || 0}`;

            XLSX.utils.sheet_add_aoa(worksheet, [[title], [totalText]], { origin: "A1" });
            worksheet["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: Object.keys(exportData[0]).length - 1 } },
            ];

            XLSX.utils.book_append_sheet(workbook, worksheet, "Profit Report");
            const fileName = `Profit_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            XLSX.writeFile(workbook, fileName);

            SuccessToast("Profit report exported successfully!");
        } catch (error) {
            console.error("Export failed:", error);
            ErrorToast("Export failed");
        }
    };

    // ✅ Handlers
    const handleFilterChange = (key, value) => {
        setFilterPayload((prev) => ({ ...prev, [key]: value }));
        setPageNumber(1);
    };

    const handleToggleFinancialYear = (event) => {
        const checked = event.target.checked;
        setIsFinancialYear(checked);
        handleFilterChange("filter_type", checked ? "FINANCIAL" : "YEAR");
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // console.log("filterPayload",filterPayload.filter_type=='year'?);
    const showYear = filterPayload.filter_type === "YEAR" && filterPayload.year
        ? dayjs(filterPayload.year).year()
        : `${filterPayload.financial_year_start} to ${filterPayload.financial_year_end}`;
    // console.log("showYear", showYear);

    return (
        <Box className="profit_report_main_container">
            {/* ---------- HEADER ---------- */}
            <div className="report-header">
                <h3 variant="h6" className="profit_report_title">
                    Profit Report
                </h3>
                {/* <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={handleExport} title="Download Excel">
                        <PiMicrosoftExcelLogoLight size={25} color="green" />
                    </IconButton>
                </Box> */}
            </div>

            {/* ---------- FILTERS ---------- */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "20% 20% 20% 20% auto",
                    alignItems: "center",
                    gap: 2,
                    marginY: 2,
                }}
            >
                {!isFinancialYear ? (
                    <>
                        {/* <FormControl>
                            <CustomAutoCompleteDropdown
                                allowClear={false}
                                readOnly={true}
                                options={[{ label: "All", value: "" }, ...months.map((m) => ({
                                    label: m.label,
                                    value: `${selectedYear}-${m.value}`,
                                }))]}
                                onChange={(e, newValue) =>
                                    handleFilterChange("month", newValue?.value)
                                }
                                value={
                                    [{ label: "All", value: "" }, ...months.map((m) => ({
                                        label: m.label,
                                        value: `${selectedYear}-${m.value}`,
                                    }))].find((m) => m.value === filterPayload.month) || null
                                }
                            />
                        </FormControl> */}

                        <FormControl>
                            <CustomYearPicker
                                readOnly={true}
                                views={["year"]}
                                maxDate={dayjs().endOf("year")}
                                value={filterPayload.year}
                                onChange={(year) => handleFilterChange("year", year)}
                            />
                        </FormControl>
                    </>
                ) : (
                    <FormControl>
                        <CustomAutoCompleteDropdown
                            allowClear={false}
                            readOnly={true}
                            options={financialYearOptions}
                            onChange={(e, newValue) => {
                                handleFilterChange("financial_year_start", newValue?.value.start);
                                handleFilterChange("financial_year_end", newValue?.value.end);
                            }}
                            value={
                                financialYearOptions.find(
                                    (fy) =>
                                        fy.value.start === filterPayload.financial_year_start &&
                                        fy.value.end === filterPayload.financial_year_end
                                ) || null
                            }
                        />
                    </FormControl>
                )}

                <FormControl>
                    <div className="expense_report_financial_year_toggle_container">
                        <Switch
                            checked={isFinancialYear}
                            onChange={handleToggleFinancialYear}
                        />
                        <p>Financial Year</p>
                    </div>
                </FormControl>
            </Box>

            <ExpandableView apiResponse={reportList} showYear={showYear} />
        </Box>
    );
};

export default ProfitReports;
